// platform-services.js — central service locator.
//
// Mirrors `Assets/_App/Platform/ServiceLocator.cs` and `PlatformBootstrap.cs`
// from the Unity port. It is the single place where engines, screens, and
// data layers reach for I/O — keeping engines pure and platform-swappable.
//
// On the web there's only one platform, so this is effectively static, but
// it preserves the architectural seam in case we later add a Capacitor
// shell or similar.

import * as contentLoader from "./data/content-loader.js";
import * as progress from "./data/progress-store.js";
import * as language from "./data/language-store.js";
import { audioPlayer } from "./core/audio-engine.js";

/**
 * @typedef {object} PlatformServices
 * @property {typeof contentLoader} content
 * @property {typeof progress}      progress
 * @property {typeof language}      language
 * @property {typeof audioPlayer}   audioPlayer
 */

/** @type {PlatformServices} */
export const services = {
  content: contentLoader,
  progress,
  language,
  audioPlayer,
};

/**
 * Boot the platform services in the order required by the Unity port's
 * AppController.Start():
 *   1. (FileSystem) — implicit on web (fetch)
 *   2. content index
 *   3. progress store (IndexedDB)
 *   4. language store (IndexedDB-backed setting)
 *
 * The content tree itself is loaded lazily — only the index is fetched at
 * boot. Items, lists, and lyrics are pulled on demand by the screens.
 */
export async function bootstrap() {
  await Promise.all([
    services.content.loadIndex().catch((e) => {
      console.warn("[platform] content index unavailable", e);
    }),
    services.progress.openProgressStore(),
    services.language.loadLanguagePreference(),
  ]);
}
